import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import InventoryService from '@/services/isomorphic/InventoryService';
import ProductService from '@/services/isomorphic/ProductService';
import { ProductAvailabilityState } from '@/services/models/Inventory';
import {
  IProduct,
  ProductModel,
  type IProductVariationMap
} from '@/services/models/Product';
import {
  VariationAttributeType,
  type IColorVariationAttribute,
  type VariationAttributeID,
  type VariationAttributeTypeFromType
} from '@/services/models/Product/variation-attributes';
import LazyValue from '@/utils/LazyValue';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import { removeUndefined } from '@/utils/object-utils';
import StaleWhileRevalidate from '@/utils/StaleWhileRevalidate';
import { computed, makeAutoObservable, runInAction } from 'mobx';
import type { Nullable } from '@/type-utils';
import { getGroupDisplayName } from '@/services/utils/product-utils';
import DisplayVariationAttributeType from '../ProductVM/DisplayVariationAttributeType';

/**
 * Type to represent color variants in a more readable way.
 */
type ColorVariants = ReadonlyArray<
  VariationAttributeTypeFromType<VariationAttributeType.Color>
>;

/** A {@link Set} which only contains the code of the variation that has stock. */
type VariationsWithStock = Set<string>;

/** The _valid_ states in which a product is considered in stock. */
const VALID_STATES_FOR_PRODUCT_DISPLAY = [
  ProductAvailabilityState.Backorder,
  ProductAvailabilityState.InStock,
  ProductAvailabilityState.Preorder
];

/** The applied refienements by the user. */
export interface IAppliedRefinements {
  /** Whether any color refinement was applied. */
  color: boolean;
  /** Whether any size refinement was applied. */
  size: boolean;
}

/**
 * View model for the product tile component. Will be used to manage the state of the product tile.
 *
 * At first, we are migrating only the color variants.
 */
export default class ProductTileVM {
  // Used to back support old usage of model directly inside ProductTile view.
  // Ideally should be fully abstracted by the view model in the future.
  private _productModel: ProductModel;
  private _colorVariants: ColorVariants;
  /** Whether to hide product(s) that are OOS. */
  private _hideOutOfStockProduct: boolean;
  /** Whether to hide offline color variants. */
  private _hideOfflineColorVariants: boolean;
  /** Used to load the inventory of the product variations and store the variation codes
   * that have stock. */
  private _lazyVariationsWithStock?: LazyValue<
    StaleWhileRevalidate<VariationsWithStock>
  >;

  /** @inheritdoc */
  @computed public get colorVariants(): ColorVariants {
    const onlineVariants = this._hideOfflineColorVariants
      ? this._colorVariants.filter(({ value }) =>
          this.determineResultingOnlineStatusOnAttributeSelection(
            DisplayVariationAttributeType.Color,
            value
          )
        )
      : this._colorVariants;

    return this._shouldUseInventory && !this._variationsWithStockLoading
      ? onlineVariants.filter(({ value }) =>
          this._isPartOfAnyVariationWithStock(value)
        )
      : onlineVariants;
  }

  /** @inheritdoc */
  public get productModel(): ProductModel {
    return this._productModel;
  }

  /** @inheritdoc */
  @computed private get variationMapEntries(): Array<
    [string, IProductVariationMap[string]]
  > {
    const entries = Object.entries(this.product.variationMap);
    // If any size refinement was selected, we want to order to display
    // a more deterministic result when the user goes to the PDP.
    return !this._appliedRefinements.size
      ? entries
      : entries.sort(([variantA], [variantB]) => {
          if (variantA < variantB) return -1;
          if (variantA > variantB) return 1;
          return 0;
        });
  }

  /**
   * Obtain the product model from the original product metadata or
   * override the selected attributes, primary and secondary image(s)
   * if there was any refinement applied.
   * @returns - The {@link ProductModel}.
   */
  private _obtainProductModel(): ProductModel {
    if (!this._appliedRefinements.color && !this._appliedRefinements.size)
      return ProductModel.from(this.product);

    let color: undefined | VariationAttributeID;
    let size: undefined | VariationAttributeID;
    for (const [variationKey, { shouldValidateWithInventory }] of this
      .variationMapEntries) {
      // Go to the next entry if it wasn't set for inventory validation
      if (!Boolean(shouldValidateWithInventory)) continue;

      // Pick the color and/or size attributes from the variation ID.
      for (const rawVariationID of variationKey.split(':')) {
        if (
          this._appliedRefinements.color &&
          rawVariationID.includes(VariationAttributeType.Color)
        ) {
          color = rawVariationID as VariationAttributeID;
        }
        if (
          this._appliedRefinements.size &&
          rawVariationID.includes(VariationAttributeType.Size)
        ) {
          size = rawVariationID as VariationAttributeID;
        }
      }
      // Exit the loop if any attribute was found.
      if (!isNullish(color) || !isNullish(size)) break;
    }
    const selectedAttributes = removeUndefined({ color, size });

    /* Pick the color if it has an existent attribute in order to retrieve the
    primary and secondary images from the color variation. */
    const selectedColor = isNullish(color)
      ? undefined
      : (Object.values(this.product.variationAttributes).find((attribute) => {
          // Ignore if its not a color attribute
          if (attribute.type !== VariationAttributeType.Color) return false;
          const colorCode = color.split('-').at(1);
          // Ignore if the color code is undefined
          if (isNullish(colorCode)) return false;
          return attribute.value.includes(colorCode);
        }) as IColorVariationAttribute);
    // Recreate the model with the selected attributes to redirect to the PDP with
    // pre-selected color and/or size and also display the respective color variant
    // images.
    return ProductModel.from({
      ...this.product,
      selectedAttributes,
      primaryImage: selectedColor?.primaryImage ?? this.product.primaryImage,
      secondaryImage:
        selectedColor?.secondaryImage ?? this.product.secondaryImage
    });
  }

  /**
   * Creates a new instance of the ProductTileVM.
   *
   * @param product - The product model to create the view model from.
   * @param _appliedRefinements - The {@link IAppliedRefinements}.
   */
  public constructor(
    private product: IProduct,
    private _appliedRefinements: IAppliedRefinements
  ) {
    this._productModel = this._obtainProductModel();
    this._colorVariants = this._productModel.getAvailableAttributesByType(
      VariationAttributeType.Color
    );
    const config = ConfigurationService.getConfig('productList');
    this._hideOutOfStockProduct = config.getSetting(
      'hideOutOfStockProduct'
    ).value;
    this._hideOfflineColorVariants = config.getSetting(
      'hideOfflineColorVariants'
    ).value;

    this._lazyVariationsWithStock = this._shouldUseInventory
      ? new LazyValue(
          () =>
            new StaleWhileRevalidate(new Set(), async () =>
              this._loadVariationsWithStock()
            )
        )
      : undefined;
    makeAutoObservable(this);
  }

  /**
   * Retrieves the product variant by SKU.
   *
   * @param sku - The SKU of the product variant to retrieve.
   * @returns The product variant.
   */
  public async selectProductVariantBySKU(sku: string): Promise<void> {
    const newProductModel = await ProductService.getProduct(sku);

    runInAction(() => {
      this._productModel = newProductModel;
    });
  }

  /** @returns The ratings of the product. */
  public get ratings(): ProductModel['ratings'] {
    return this.productModel.ratings;
  }

  /**
   * Whether the inventory information is loading.
   * @returns - `true` when the information is loading.
   */
  @computed private get _variationsWithStockLoading(): boolean {
    return this._lazyVariationsWithStock?.value?.pending ?? false;
  }

  /**
   * Flatten the {@link LazyValue} and {@link StaleWhileRevalidate} to obtain the {@link VariationsWithStock}.
   * @returns - {@link VariationsWithStock}.
   */
  @computed private get _variationsWithStock(): VariationsWithStock {
    return this._lazyVariationsWithStock?.value?.value ?? new Set();
  }

  /**
   * Search for the existence given value as part of any {@link VariationsWithStock}.
   * @param value - The value to search for.
   * @returns - `true` if the given value is part of any {@link VariationsWithStock}.
   */
  private _isPartOfAnyVariationWithStock(value: string): boolean {
    for (const variantWithStock of this._variationsWithStock) {
      if (variantWithStock.includes(value)) return true;
    }
    return false;
  }

  /**
   * Whether the product has any stock available. If the VM is set to display products
   * that could be OOS, this will always return `true`, otherwise it will check the
   * existence of at least one variation with stock.
   * @returns - Whether the product has any stock available.
   */
  @computed public get productHasAnyStock(): boolean {
    if (!this._shouldUseInventory) return true;

    return this._variationsWithStockLoading
      ? true
      : this._variationsWithStock.size > 0;
  }

  /**
   * Retrieve the product's UPCs that should be checked for stock availability.
   * @returns - The array of UPCs.
   */
  @computed private get _productUPCs(): ReadonlyArray<string> {
    return this.variationMapEntries.reduce<ReadonlyArray<string>>(
      (acc, [_, { upc, shouldValidateWithInventory }]) => {
        if (isNullOrEmpty(upc)) return acc;
        return shouldValidateWithInventory ? [upc, ...acc] : acc;
      },
      []
    );
  }

  /**
   * Load the product's inventory using the variant UPCs.
   * @returns - {@link HasAnyStockByVariantCode}.
   */
  @computed
  private async _loadVariationsWithStock(): Promise<VariationsWithStock> {
    const variationsWithStock = new Set<string>();

    // Retrieve the inventory for the UPCs in order to calculate their stock.
    const inventoryByUpc = await InventoryService.bulkGetProductInventory(
      ...this._productUPCs
    );
    for (const [variation, { upc }] of this.variationMapEntries) {
      // Skip entry if there's no UPC
      if (isNullOrEmpty(upc)) continue;

      const upcInventory = inventoryByUpc[upc];
      // Skip entry if there's inventory
      if (isNullish(upcInventory)) continue;

      const variationHasStock = VALID_STATES_FOR_PRODUCT_DISPLAY.includes(
        upcInventory.availabilityState
      );
      if (variationHasStock) variationsWithStock.add(variation);
    }
    return variationsWithStock;
  }

  /**
   * Whether the tile should use inventory information if it should hide
   * OOS products and the user has selected any filter.
   * @returns - True if the VM should use inventory information.
   */
  private get _shouldUseInventory(): boolean {
    const shouldCheckStockOfAnyVariation = this.variationMapEntries.some(
      ([_, { shouldValidateWithInventory }]) =>
        Boolean(shouldValidateWithInventory)
    );
    return this._hideOutOfStockProduct && shouldCheckStockOfAnyVariation;
  }

  /**
   * Placeholder that returns the group display name
   * like the ProductVM does. Once the ProductTileVM fully
   * abstracts the use of the ProductModel to the VM,
   * this method can be removed.
   * @see ProductVM.groupDisplayName
   * @returns - The group display name.
   */
  public get groupDisplayName(): Nullable<string> {
    const group = this.productModel.gender ?? this.productModel.group;
    return getGroupDisplayName(group);
  }

  /**
   * Given a variation type and attribute, determine the online status of the
   * product that would result if the aforementioned attribute were selected.
   *
   * @param variationType - The type of the variation to potentially select.
   * @param value - The value of the variation to potentially select.
   *
   * @returns The online status of the potential product post-selection. Will
   * return `false` if there's no product that satisfies the selection.
   */
  public determineResultingOnlineStatusOnAttributeSelection(
    variationType: DisplayVariationAttributeType,
    value: string
  ): boolean {
    const variationKey = `${variationType}-${value}`;
    return this.product.variationMap[variationKey]?.isOnline ?? false;
  }
}
